import cx from 'classnames';
import {Form, Formik, FormikProps} from 'formik';
import {includes} from 'lodash';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    AiProviderSelect, Button, Card, NumericFieldFormik, PredefinedNotifierType, SelectFormik, ToggleFormik,
    UnsavedChangesLeavePrompt
} from 'components';
import {ActivityAiBlock, ExternalIntegrationPurpose, ValidationCode, VoiceInputMode} from 'enums';
import {useApiData} from 'hooks';
import {SessionActivityName} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ActivityAiSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';
import {buildTranslationKey} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.ACTIVITIES;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

// One grid per row, not per card: the fixed tracks still line every provider dropdown up across all cards, but
// each row can collapse on its own below md. Aligned to the bottom because a select is taller than a toggle and
// the field's underline is the strongest horizontal line in this design system.
const ROW_CLASS = 'tw-grid tw-items-end tw-gap-x-4 tw-gap-y-3 md:tw-min-h-[3rem] ' +
    'md:tw-grid-cols-[14rem_12rem_minmax(0,1fr)]';
const GROUP_CLASS = 'tw-flex tw-flex-col md:tw-gap-3 -md:tw-gap-6';
const GROUP_TITLE_CLASS = 'tw-ts-overline tw-text-default-dimmed tw-mb-2';
// A rule closes the group: without it the gap between groups equals the gap inside one, and the heading reads
// as if it covered every row below it.
const GROUP_SEPARATED_CLASS = 'tw-mt-4 tw-pt-4 tw-border-t tw-border-default';

type Props = {
    activity: SessionActivityName;
    blocks: ActivityAiBlock[];
    settings: ActivityAiSettings;
    providers: Partial<Record<ExternalIntegrationPurpose, string[]>>;
    onSaved: () => void;
};

type FormTemplateProps = {
    loading: boolean;
} & Props & FormikProps<ActivityAiSettings>;

const FormTemplate: React.FC<FormTemplateProps> = ({
    activity, blocks, providers, loading, values, initialValues, isValid, submitForm
}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [values, initialValues]);

    const has = useCallback((block: ActivityAiBlock) => includes(blocks, block), [blocks]);

    const isTextChat = useMemo(() => activity === SessionActivityName.TextChat, [activity]);

    const hasSpeechToText = useMemo(
        () => has(ActivityAiBlock.Transcription) || has(ActivityAiBlock.StreamAudio),
        [has]
    );
    const hasFeatures = useMemo(
        () => has(ActivityAiBlock.VoiceReply) || has(ActivityAiBlock.ConversationSummary)
            || has(ActivityAiBlock.Compliance) || has(ActivityAiBlock.ChatbotEndSession) || has(ActivityAiBlock.Provider),
        [has]
    );

    const voiceInputModeOptions = useMemo(() => [
        {id: VoiceInputMode.PushToTalk, name: t(T_KEYS.LABELS.PUSH_TO_TALK)},
        {id: VoiceInputMode.Toggle, name: t(T_KEYS.LABELS.TOGGLE)}
    ], [t]);

    // Audio activities screen the spoken conversation, the text chat screens typed messages — different purposes,
    // same block, so the provider list has to follow the activity.
    const compliancePurpose = useMemo(() => isTextChat
        ? ExternalIntegrationPurpose.TextChatCompliance
        : ExternalIntegrationPurpose.AudioCompliance, [isTextChat]);

    return (
        <Form>
            {hasSpeechToText &&
                <Fragment>
                    <div className={GROUP_TITLE_CLASS}>{t(T_KEYS.LABELS.SPEECH_TO_TEXT)}</div>
                    <div className={GROUP_CLASS}>
                        {has(ActivityAiBlock.Transcription) &&
                            <div className={ROW_CLASS}>
                                <ToggleFormik
                                    name='transcription.enabled'
                                    label={t(T_KEYS.LABELS.TRANSCRIPTION)}
                                    idPrefix={activity}
                                />
                                <AiProviderSelect
                                    name='transcription.provider'
                                    providers={providers[ExternalIntegrationPurpose.STT]}
                                    placeholder={t(T_KEYS.LABELS.PROVIDER)}
                                    disabled={!values.transcription?.enabled}
                                    required={values.transcription?.enabled}
                                />
                                <ToggleFormik
                                    name='transcription.keepOnFinish'
                                    label={t(T_KEYS.LABELS.KEEP_ON_FINISH)}
                                    idPrefix={activity}
                                    disabled={!values.transcription?.enabled}
                                />
                            </div>}

                        {has(ActivityAiBlock.StreamAudio) &&
                            <div className={ROW_CLASS}>
                                <ToggleFormik
                                    name='streamAudio.enabled'
                                    label={t(T_KEYS.LABELS.STREAM_AUDIO)}
                                    idPrefix={activity}
                                />
                                {isTextChat &&
                                    <SelectFormik
                                        className='md:tw-col-start-3 md:tw-max-w-48'
                                        name='streamAudio.mode'
                                        options={voiceInputModeOptions}
                                        valueAs='id'
                                        placeholder={t(T_KEYS.LABELS.VOICE_INPUT_MODE)}
                                        disabled={!values.streamAudio?.enabled}
                                    />}
                            </div>}
                    </div>
                </Fragment>}

            {hasFeatures &&
                <div className={cx(GROUP_CLASS, {[GROUP_SEPARATED_CLASS]: hasSpeechToText})}>
                    {has(ActivityAiBlock.VoiceReply) &&
                        <div className={ROW_CLASS}>
                            <ToggleFormik
                                name='voiceReply.enabled'
                                label={t(T_KEYS.LABELS.VOICE_REPLY)}
                                idPrefix={activity}
                            />
                            <AiProviderSelect
                                name='voiceReply.provider'
                                providers={providers[ExternalIntegrationPurpose.TTS]}
                                placeholder={t(T_KEYS.LABELS.PROVIDER)}
                                disabled={!values.voiceReply?.enabled}
                                required={values.voiceReply?.enabled}
                            />
                            <ToggleFormik
                                name='voiceReply.welcomeMessage'
                                label={t(T_KEYS.LABELS.WELCOME_MESSAGE)}
                                idPrefix={activity}
                                disabled={!values.voiceReply?.enabled}
                            />
                        </div>}

                    {has(ActivityAiBlock.ConversationSummary) &&
                        <div className={ROW_CLASS}>
                            <ToggleFormik
                                name='conversationSummary.enabled'
                                label={t(T_KEYS.LABELS.CONVERSATION_SUMMARY)}
                                idPrefix={activity}
                            />
                            <AiProviderSelect
                                name='conversationSummary.provider'
                                providers={providers[ExternalIntegrationPurpose.ConversationSummary]}
                                placeholder={t(T_KEYS.LABELS.PROVIDER)}
                                disabled={!values.conversationSummary?.enabled}
                                required={values.conversationSummary?.enabled}
                            />
                        </div>}

                    {has(ActivityAiBlock.Compliance) &&
                        <div className={ROW_CLASS}>
                            <ToggleFormik
                                name='compliance.enabled'
                                label={t(T_KEYS.LABELS.COMPLIANCE)}
                                idPrefix={activity}
                            />
                            <AiProviderSelect
                                name='compliance.provider'
                                providers={providers[compliancePurpose]}
                                placeholder={t(T_KEYS.LABELS.PROVIDER)}
                                disabled={!values.compliance?.enabled}
                                required={values.compliance?.enabled}
                            />
                            {!isTextChat &&
                                <div className='tw-flex tw-gap-4'>
                                    <NumericFieldFormik
                                        name='compliance.windowSeconds'
                                        label={t(T_KEYS.LABELS.WINDOW_SECONDS)}
                                        min={5}
                                        max={30}
                                        className='tw-w-32'
                                        disabled={!values.compliance?.enabled}
                                    />
                                    <NumericFieldFormik
                                        name='compliance.hintDurationSeconds'
                                        active
                                        label={t(T_KEYS.LABELS.HINT_DURATION_SECONDS)}
                                        placeholder={t(T_KEYS.LABELS.UNTIL_CLOSED)}
                                        min={1}
                                        max={60}
                                        className='tw-w-32'
                                        disabled={!values.compliance?.enabled}
                                    />
                                </div>}
                            {isTextChat &&
                                <ToggleFormik
                                    name='compliance.checkBotMessages'
                                    label={t(T_KEYS.LABELS.CHECK_BOT_MESSAGES)}
                                    idPrefix={activity}
                                    disabled={!values.compliance?.enabled}
                                />}
                        </div>}

                    {has(ActivityAiBlock.ChatbotEndSession) &&
                        <div className={ROW_CLASS}>
                            <ToggleFormik
                                name='chatbotEndSession'
                                label={t(T_KEYS.LABELS.CHATBOT_END_SESSION)}
                                idPrefix={activity}
                            />
                        </div>}

                    {has(ActivityAiBlock.Provider) &&
                        <div className={ROW_CLASS}>
                            <ToggleFormik name='enabled' label={t(T_KEYS.LABELS.ENABLED)} idPrefix={activity} />
                            <AiProviderSelect
                                name='provider'
                                providers={providers[ExternalIntegrationPurpose.AgentChatbot]}
                                placeholder={t(T_KEYS.LABELS.PROVIDER)}
                                disabled={!values.enabled}
                                required={values.enabled}
                            />
                            <ToggleFormik
                                name='storeHistory'
                                label={t(T_KEYS.LABELS.STORE_HISTORY)}
                                idPrefix={activity}
                                disabled={!values.enabled}
                            />
                        </div>}
                </div>}

            <div className='tw-text-right tw-mt-controls'>
                <Button
                    color='primary'
                    disabled={hasNoChanges || !isValid}
                    loading={loading}
                    onClick={submitForm}
                >
                    {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                </Button>
            </div>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const ActivitySection: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const {activity, settings, onSaved} = props;

    const {send: save, loading} = useApiData(
        settingsApi.updateActivityAi, {notificationType: PredefinedNotifierType.Save, onSuccess: onSaved}
    );

    const handleSubmit = useCallback((values: ActivityAiSettings) => save(activity, values), [save, activity]);

    // An enabled feature without a provider is enabled and silently dead, so the provider becomes required the
    // moment its own toggle goes on. Blocks the activity does not have stay absent and are simply not validated.
    const validationSchema = useMemo(() => {
        const providerRequiredWhenEnabled = () => Yup.string().nullable().when('enabled', {
            is: true,
            then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required}))
        });
        const windowSecondsRequiredWhenEnabled = () => Yup.number().nullable().when('enabled', {
            is: true,
            then: (schema) => schema
                .required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required}))
                .min(5, apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Minimum, expected: 5}))
                .max(30, apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Maximum, expected: 30}))
        });

        return Yup.object().shape({
            transcription: Yup.object().shape({provider: providerRequiredWhenEnabled()}),
            voiceReply: Yup.object().shape({provider: providerRequiredWhenEnabled()}),
            conversationSummary: Yup.object().shape({provider: providerRequiredWhenEnabled()}),
            compliance: Yup.object().shape({
                provider: providerRequiredWhenEnabled(),
                // windowSeconds and hintDurationSeconds are only rendered for non-text-chat activities
                // (checkBotMessages takes their place for text chat), so they're only validated there too.
                ...(activity !== SessionActivityName.TextChat && {
                    windowSeconds: windowSecondsRequiredWhenEnabled(),
                    // Empty is a valid choice here — it keeps the notification up until the agent closes it.
                    hintDurationSeconds: Yup.number().nullable()
                        .min(1, apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Minimum, expected: 1}))
                        .max(60, apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Maximum, expected: 60}))
                })
            }),
            // The agent chatbot activity keeps enabled and provider flat on the activity itself.
            provider: providerRequiredWhenEnabled()
        });
    }, [t, activity]);

    return (
        <Card className='tw-mb-4'>
            <div className='tw-ts-h5-bold tw-mb-4'>
                {t(COMMON_T_KEYS.SESSION.ACTIVITY[buildTranslationKey(activity)])}
            </div>
            <Formik
                initialValues={settings || {}}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {(formikProps: FormikProps<ActivityAiSettings>) =>
                    <FormTemplate {...props} {...formikProps} loading={loading} />}
            </Formik>
        </Card>
    );
};
